/**
 * Regras da Verificação (KYC) da conta da marca — lógica pura, sem React,
 * pra ficar testável. A tela (`pages/KycPage.tsx`) só desenha o que sai daqui.
 *
 * O que mora aqui e POR QUÊ (cada item nasceu de um erro da tela equivalente
 * do brand-app, que esta versão já nasce sem):
 *
 *  1. Visibilidade por CREDENCIAL Asaas, nunca por `payment_provider`. Na
 *     migração Zoop → Asaas a marca tem as duas carteiras e a coluna legada
 *     segue 'zoop' de propósito — gatear por ela esconde a tela de quem mais
 *     precisa dela. Quem decide é `settings.providers` (SRV-133), a mesma
 *     lista que alimenta o seletor de provedor.
 *  2. O CANAL de envio é por documento: com `onboarding_url` vai pelo link do
 *     parceiro, sem link é upload direto. Os dois convivem na MESMA lista —
 *     esconder uma coisa atrás da outra é o que fazia a pendência de procuração
 *     desaparecer enquanto a identificação ainda usava link.
 *  3. `REJECTED` reabre o envio e mostra o motivo (`reject_reasons`). Documento
 *     recusado que foi por link ganha um link NOVO da Asaas, então a ação dele
 *     continua sendo o link — não um upload que a Asaas vai recusar.
 *  4. Cabeçalho não diz "em análise" havendo documento recusado com `general`
 *     ainda pendente: nesse estado a marca TEM o que fazer.
 *  5. Link tem validade. Link vencido não é oferecido como ação sem aviso.
 */
import type {
  KycAccountBlockStatus,
  KycAccountStatus,
  KycDocumentGroup,
  KycDocumentStatus,
  KycSellerStatus,
  KycUploadSlot,
} from '../types/repasses';
import { availableProviders } from './provider';

const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const lower = (value: unknown): string => String(value ?? '').trim().toLowerCase();

// ── 1. Visibilidade: a marca TEM credencial Asaas? ────────────────
/** Só o que o gate precisa do `GET /settings`. */
export type KycGateSettings =
  | {
      payment_provider?: string | null;
      providers?: string[] | null;
      asaas?: { provisioned?: boolean | null } | null;
    }
  | null
  | undefined;

/**
 * A tela existe pra marca que TEM credencial Asaas — não pra marca cujo
 * provedor padrão é Asaas, e a diferença não é teórica: na migração a
 * Americanas tem as duas carteiras e a coluna legada `payment_provider` segue
 * 'zoop' de propósito. Perguntar pelo padrão esconderia a tela exatamente de
 * quem precisa aprovar o KYC Asaas.
 *
 * Fonte: `providers` (SRV-133), a mesma lista do seletor de provedor. As duas
 * quedas abaixo só valem quando `providers` NÃO veio (BFF antigo) — nunca pra
 * tirar da tela uma marca que `providers` já incluiu.
 */
export const hasAsaasCredential = (settings: KycGateSettings): boolean => {
  if (!settings) return false;
  const providers = availableProviders(settings);
  if (providers.length > 0) return providers.includes('asaas');
  if (settings.asaas?.provisioned === true) return true;
  return lower(settings.payment_provider) === 'asaas';
};

// ── Status da conta (Pay) ─────────────────────────────────────────
export const normalizeSellerStatus = (
  status: KycSellerStatus | null | undefined,
): string | null => lower(status) || null;

export const isSellerBlocked = (status: KycSellerStatus | null | undefined): boolean => {
  const value = normalizeSellerStatus(status);
  return value === 'denied' || value === 'disabled';
};

const SELLER_STATUS_LABELS: Record<string, string> = {
  active: 'Aprovado',
  pending: 'Em análise',
  under_review: 'Em análise',
  denied: 'Negado',
  disabled: 'Desativado',
};

export const sellerStatusLabel = (
  status: KycSellerStatus | null | undefined,
): string => {
  const value = normalizeSellerStatus(status);
  if (!value) return '—';
  return SELLER_STATUS_LABELS[value] ?? value;
};

const ACCOUNT_BLOCK_LABELS: Record<string, string> = {
  APPROVED: 'aprovado',
  PENDING: 'pendente',
  AWAITING_APPROVAL: 'em análise',
  REJECTED: 'recusado',
};

/** Bloco do `accountStatus` da Asaas → texto de tela. */
export const accountBlockLabel = (
  block: KycAccountBlockStatus | null | undefined,
): string | null => {
  const value = upper(block);
  if (!value) return null;
  return ACCOUNT_BLOCK_LABELS[value] ?? String(block);
};

/** `general === APPROVED` — a Asaas liberou a conta. */
export const isGeneralApproved = (
  accountStatus: KycAccountStatus | null | undefined,
): boolean => upper(accountStatus?.general) === 'APPROVED';

// ── Documentos ────────────────────────────────────────────────────
const DOC_STATUS_LABELS: Record<string, string> = {
  NOT_SENT: 'Não enviado',
  PENDING: 'Em análise',
  APPROVED: 'Aprovado',
  REJECTED: 'Recusado',
  IGNORED: 'Dispensado',
};

export const docStatusLabel = (
  status: KycDocumentStatus | null | undefined,
): string => {
  const value = upper(status);
  if (!value) return 'Não enviado';
  return DOC_STATUS_LABELS[value] ?? String(status);
};

export const docStatusTone = (
  status: KycDocumentStatus | null | undefined,
): 'success' | 'wait' | 'urgent' | 'neutral' => {
  switch (upper(status)) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'wait';
    case 'REJECTED':
      return 'urgent';
    default:
      return 'neutral';
  }
};

export const isDocRejected = (
  status: KycDocumentStatus | null | undefined,
): boolean => upper(status) === 'REJECTED';

/**
 * Só faz sentido enviar quando a Asaas ainda não recebeu (`NOT_SENT`/vazio) ou
 * recusou (`REJECTED` → o envio REABRE). `PENDING` ali é "em análise".
 */
export const isDocUploadable = (
  status: KycDocumentStatus | null | undefined,
): boolean => {
  const value = upper(status);
  return value === '' || value === 'NOT_SENT' || value === 'REJECTED';
};

/** A Asaas devolve este UUID nulo enquanto o grupo não aceita upload por API. */
const PLACEHOLDER_DOCUMENT_ID = '00000000-0000-0000-0000-000000000000';

export const isUploadableDocumentId = (
  id: string | null | undefined,
): id is string => {
  const trimmed = (id ?? '').trim();
  return !!trimmed && trimmed !== PLACEHOLDER_DOCUMENT_ID;
};

/** Grupos cujo upload direto vai pelos slots (RG frente/verso + selfie). */
const IDENTITY_DOC_TYPES = new Set(['IDENTIFICATION', 'IDENTIFICATION_SELFIE']);

export const isIdentityDocType = (type: string | null | undefined): boolean =>
  IDENTITY_DOC_TYPES.has(upper(type));

// ── 5. Validade do link do parceiro ───────────────────────────────
/**
 * Fim do dia em BRT (UTC-3), contado desde a meia-noite UTC da mesma data.
 * São Paulo não tem horário de verão desde 2019, então o deslocamento é fixo.
 */
const END_OF_DAY_BRT_MS = 86_399_999 + 3 * 3_600_000;

export interface LinkExpiry {
  /** ISO que veio no documento (null = a Asaas não disse a validade). */
  expiresAt: string | null;
  /** true só quando SABEMOS que venceu. Sem data, não é vencido. */
  expired: boolean;
}

export const linkExpiry = (
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): LinkExpiry => {
  const raw = (expiresAt ?? '').trim();
  if (!raw) return { expiresAt: null, expired: false };
  const parsed = Date.parse(raw);
  // Data que não dá pra ler não vira "vencido" — vencer link válido é pior
  // que deixar passar um vencido com aviso.
  if (Number.isNaN(parsed)) return { expiresAt: raw, expired: false };
  // Data PURA (`YYYY-MM-DD`) vale o DIA inteiro. `Date.parse` dela dá
  // meia-noite UTC, que são 21h de BRT do dia ANTERIOR: comparar com isso
  // esconderia um link que ainda vale. O pay-api documenta o campo como ISO
  // completo e é o que vem nos testes dele, mas não há como provar o formato
  // de produção — e falhar escondendo link válido é pior que avisar tarde.
  const end = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? parsed + END_OF_DAY_BRT_MS
    : parsed;
  return { expiresAt: raw, expired: end < now };
};

// ── 2 + 3. Ação de cada documento ─────────────────────────────────
export type KycDocActionKind =
  /** Vai pelo link do parceiro (a Asaas recusa upload por API neste grupo). */
  | 'link'
  /** Link vencido: oferecer link novo passa por "Atualizar". */
  | 'link_expired'
  /** Upload direto pela tela (grupo sem link). */
  | 'upload'
  /** Identificação sem link: 3 arquivos em 2 grupos → pelos slots. */
  | 'slots'
  /** A Asaas ainda não liberou o grupo pra upload por API (UUID nulo). */
  | 'not_ready'
  /** Nada a fazer: em análise, aprovado ou dispensado. */
  | 'none';

export interface KycDocAction {
  kind: KycDocActionKind;
  url: string | null;
  expiry: LinkExpiry;
}

/**
 * O que a marca pode fazer NESTE documento agora.
 *
 * A ordem importa: o canal (link x upload) é decidido ANTES do tipo. Documento
 * recusado que foi por link ganha um link NOVO da Asaas, e é ele que resolve a
 * pendência — oferecer upload ali só renderia um erro da Asaas.
 */
export const docAction = (
  doc: Pick<
    KycDocumentGroup,
    'id' | 'type' | 'status' | 'onboarding_url' | 'onboarding_url_expires_at'
  >,
  now: number = Date.now(),
): KycDocAction => {
  const expiry = linkExpiry(doc.onboarding_url_expires_at, now);
  const url = (doc.onboarding_url ?? '').trim() || null;
  if (!isDocUploadable(doc.status)) return { kind: 'none', url, expiry };
  if (url) {
    return { kind: expiry.expired ? 'link_expired' : 'link', url, expiry };
  }
  if (isIdentityDocType(doc.type)) return { kind: 'slots', url: null, expiry };
  if (!isUploadableDocumentId(doc.id)) {
    return { kind: 'not_ready', url: null, expiry };
  }
  return { kind: 'upload', url: null, expiry };
};

/**
 * Mesma decisão de canal, mas pra um SLOT (um dos 3 arquivos da
 * identificação). Nunca devolve `slots` — o slot é a folha. A selfie pode
 * herdar o link da identificação, então ela cai em `link` como qualquer outro.
 */
export const slotAction = (
  slot: Pick<
    KycUploadSlot,
    'document_id' | 'status' | 'onboarding_url' | 'onboarding_url_expires_at'
  >,
  now: number = Date.now(),
): KycDocAction => {
  const expiry = linkExpiry(slot.onboarding_url_expires_at, now);
  const url = (slot.onboarding_url ?? '').trim() || null;
  if (!isDocUploadable(slot.status)) return { kind: 'none', url, expiry };
  if (url) {
    return { kind: expiry.expired ? 'link_expired' : 'link', url, expiry };
  }
  if (!isUploadableDocumentId(slot.document_id)) {
    return { kind: 'not_ready', url: null, expiry };
  }
  return { kind: 'upload', url: null, expiry };
};

/** Os slots que enviam ESTE grupo de identificação (por `type`). */
export const slotsForDoc = (
  doc: Pick<KycDocumentGroup, 'type'>,
  slots: KycUploadSlot[],
): KycUploadSlot[] => slots.filter((s) => upper(s.type) === upper(doc.type));

// ── 4. Cabeçalho ──────────────────────────────────────────────────
export type KycHeadlineKind =
  | 'approved'
  | 'rejected'
  | 'blocked'
  | 'action_required'
  | 'awaiting_review'
  | 'unknown';

export interface KycHeadline {
  kind: KycHeadlineKind;
  tone: 'success' | 'wait' | 'urgent' | 'info';
  title: string;
  body: string;
}

export interface KycHeadlineInput {
  sellerStatus: KycSellerStatus | null | undefined;
  accountStatus: KycAccountStatus | null | undefined;
  documents: Array<Pick<KycDocumentGroup, 'id' | 'type' | 'status' | 'onboarding_url' | 'onboarding_url_expires_at'>>;
  rejectReasons?: string | null;
  now?: number;
}

/**
 * O estado que o cabeçalho anuncia.
 *
 * A recusa vem ANTES de "em análise" de propósito: com documento `REJECTED` e
 * `general` ainda pendente, a conta está sim travada esperando algo — só que
 * esse algo é a marca reenviar. Dizer "em análise" ali é mandar esperar por
 * uma análise que não vai acontecer.
 */
export const kycHeadline = ({
  sellerStatus,
  accountStatus,
  documents,
  rejectReasons,
  now = Date.now(),
}: KycHeadlineInput): KycHeadline => {
  const generalApproved = isGeneralApproved(accountStatus);
  const status = normalizeSellerStatus(sellerStatus);
  const rejected = documents.filter((d) => isDocRejected(d.status));
  const motivo = (rejectReasons ?? '').trim();

  if (rejected.length > 0 && !generalApproved) {
    return {
      kind: 'rejected',
      tone: 'urgent',
      title:
        rejected.length === 1
          ? 'Documento recusado — reenvie'
          : `${rejected.length} documentos recusados — reenvie`,
      body: motivo
        ? `A Asaas recusou e pediu reenvio. Motivo informado: ${motivo}`
        : 'A Asaas recusou e pediu reenvio. Veja abaixo quais documentos reabriram.',
    };
  }

  if (isSellerBlocked(status)) {
    return {
      kind: 'blocked',
      tone: 'urgent',
      title: status === 'disabled' ? 'Conta desativada' : 'Cadastro negado',
      body: motivo
        ? `Regularize os documentos abaixo para retomar pagamentos e recargas. Motivo informado: ${motivo}`
        : 'Regularize os documentos abaixo para retomar pagamentos e recargas.',
    };
  }

  if (status === 'active' || (!status && generalApproved)) {
    return {
      kind: 'approved',
      tone: 'success',
      title: 'Cadastro aprovado',
      body: 'A documentação da marca foi aceita. Pagamentos e recargas estão liberados.',
    };
  }

  const pendente = documents.some((d) => docAction(d, now).kind !== 'none');
  if (pendente) {
    return {
      kind: 'action_required',
      tone: 'wait',
      title: 'Documentos pendentes',
      body: 'Envie os documentos abaixo para liberar pagamentos e recargas da marca.',
    };
  }

  if (documents.length > 0) {
    return {
      kind: 'awaiting_review',
      tone: 'info',
      title: 'Documentos em análise',
      body: 'A Asaas está analisando o cadastro — não precisa enviar mais nada. Pagamentos e recargas liberam sozinhos quando aprovar.',
    };
  }

  return {
    kind: 'unknown',
    tone: 'info',
    title: 'Cadastro em verificação',
    body: 'Nenhum documento listado ainda. Aguarde alguns segundos após a criação da conta e clique em Atualizar.',
  };
};

/** `File` → base64 puro (sem o prefixo `data:`, que o pay-api não quer). */
export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Falha ao ler o arquivo'));
    reader.readAsDataURL(file);
  });
